import React, { useEffect, useState, useMemo } from "react";
import { Card, Badge, Button } from "react-bootstrap";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";
import { useLanguage } from "../contexts/LanguageContext";
import th from "../locales/islamic/th";
import jawi from "../locales/islamic/jawi";
import {
  DAYS, teacherLabel, subjectCode, buildGrid, shortTime,
  exportTimetablePdf, printTimetableDoc, docOptions,
} from "../utils/islamicTimetable";

const API = process.env.REACT_APP_API;

// ตารางสอนของห้องที่ตัวเองเป็นครูประจำชั้น — อ่านอย่างเดียว การจัดตารางอยู่ที่
// ฝ่ายการสอน (/islamic/timetable-editor) ห้องมาจาก JWT ฝั่ง server ไม่ใช่ ir_id
// ที่หน้านี้ส่งไป
export default function IslamicAdvisorTimetable() {
  const { setLoading } = useLoading();
  const { lang } = useLanguage();
  const t = lang === "th" ? th : jawi;
  const tt = t.advisorTimetable;

  const [yearId, setYearId] = useState(() => localStorage.getItem("yearId") || null);
  const [yearList, setYearList] = useState([]);
  const [room, setRoom] = useState(null);
  const [noRoom, setNoRoom] = useState(false);
  const [timetable, setTimetable] = useState([]);
  const [periods, setPeriods] = useState([]);

  useEffect(() => {
    axiosInstance.get(`${API}/getStudyYear`)
      .then((res) => {
        const list = res.data;
        setYearList(list);
        let id = localStorage.getItem("yearId");
        if (!id && list.length > 0) { id = list[0].year_id; localStorage.setItem("yearId", id); }
        setYearId(id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!yearId) return;
    setLoading(true);
    setNoRoom(false);
    axiosInstance.get(`${API}/islamic-advisor/roomTimetable/${yearId}`)
      .then((res) => { setRoom(res.data.room); setTimetable(res.data.timetable || []); })
      .catch((err) => {
        setRoom(null);
        setTimetable([]);
        if (err.response?.status === 404) setNoRoom(true);
      })
      .finally(() => setLoading(false));
  }, [yearId, setLoading]);

  useEffect(() => {
    if (!yearId) return;
    axiosInstance.get(`${API}/getPeriodList/${yearId}`)
      .then((res) => setPeriods(res.data.map((p) => ({
        ...p,
        period_begin: shortTime(p.period_begin),
        period_end: shortTime(p.period_end),
      }))))
      .catch(() => setPeriods([]));
  }, [yearId]);

  const grid = useMemo(() => buildGrid(timetable), [timetable]);

  // ─── พิมพ์ตารางสอน — เอกสารตามภาษาเมนู (ดู docOptions ใน utils/islamicTimetable) ──
  const printDoc = () => {
    const label = `${room.ic_class}/${room.ir_no}`;
    return {
      ...docOptions(lang, t, tt),
      title: tt.docTitle(label),
      subtitle: t.common.studyYear(room.year_year),
      periods,
      cellHtml: (day, period) => {
        const slot = grid[day]?.[period.period_id];
        if (!slot) return "";
        return `<div style="font-weight:600;font-size:10px;">${subjectCode(slot)}</div>
                <div style="font-size:11px;color:#333;">${slot.isubject_name || ""}</div>
                <div style="font-size:10px;color:#555;">${teacherLabel(lang, slot).name}</div>`;
      },
      filename: `${tt.docFile(label.replace("/", "-"))}.pdf`,
    };
  };

  return (
    <React.Fragment>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h4 className="main-title mb-0">{tt.pageTitle}</h4>
        <select
          className="form-select w-auto"
          value={yearId || ""}
          onChange={(e) => { localStorage.setItem("yearId", e.target.value); setYearId(e.target.value); }}
        >
          {yearList.map((y) => (
            <option key={y.year_id} value={y.year_id}>{t.common.studyYear(y.year_year)}</option>
          ))}
        </select>
      </div>

      {noRoom && (
        <Card className="card-one">
          <Card.Body className="text-center text-secondary py-5">
            <i className="ri-information-line fs-3 d-block mb-2"></i>
            {t.advisorHome.noRoom}
          </Card.Body>
        </Card>
      )}

      {room && (
        <Card className="card-one">
          <Card.Header className="d-flex align-items-center gap-2 flex-wrap">
            <span className="fw-semibold">{t.common.classNo(room.ic_class, room.ir_no)}</span>
            <Badge bg="info">{room.is_programCode}</Badge>
            <Badge bg={room.ir_gender === "m" ? "primary" : "danger"}>
              {room.ir_gender === "m" ? t.common.male : t.common.female}
            </Badge>
            <div className="d-flex align-items-center gap-2 ms-auto">
              <Button
                variant="outline-danger"
                size="sm"
                disabled={periods.length === 0}
                onClick={() => exportTimetablePdf(printDoc())}
              >
                <i className="ri-download-2-line me-1"></i>{tt.exportPdf}
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={periods.length === 0}
                onClick={() => printTimetableDoc(printDoc())}
              >
                <i className="ri-printer-line me-1"></i>{tt.print}
              </Button>
            </div>
          </Card.Header>
          <Card.Body className="p-2" style={{ overflowX: "auto" }}>
            {periods.length === 0 ? (
              <div className="text-center text-muted py-5">{tt.noPeriods}</div>
            ) : (
              <table
                className="table table-bordered mb-0"
                style={{ tableLayout: "fixed", minWidth: periods.length * 100 + 80 }}
              >
                <thead className="table-secondary">
                  <tr>
                    <th style={{ width: 80, fontSize: "0.78rem" }}>{tt.colDay}</th>
                    {periods.map((p) => (
                      <th key={p.period_id} className="text-center" style={{ fontSize: "0.73rem" }}>
                        <div className="fw-semibold">{tt.periodNo(p.period_order)}</div>
                        <div className="text-muted fw-normal">{p.period_begin}–{p.period_end}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {DAYS.map((d) => (
                    <tr key={d}>
                      <td className="text-center align-middle fw-semibold" style={{ fontSize: "0.78rem" }}>
                        {t.days[d]}
                      </td>
                      {periods.map((p) => {
                        const slot = grid[d]?.[p.period_id];
                        const teacher = teacherLabel(lang, slot);
                        return (
                          <td key={p.period_id} style={{ padding: 2, verticalAlign: "top" }}>
                            {slot ? (
                              <div className="rounded p-1 bg-primary text-white" style={{ fontSize: "0.72rem" }}>
                                <div className="fw-semibold" style={{ lineHeight: 1.2, fontSize: "0.66rem" }}>
                                  {subjectCode(slot)}
                                </div>
                                <div className="arabic-text" style={{ lineHeight: 1.3, opacity: 0.9 }}>
                                  {slot.isubject_name}
                                </div>
                                <div
                                  className={teacher.arabic ? "arabic-text" : ""}
                                  style={{ lineHeight: 1.2, opacity: 0.75 }}
                                >
                                  {teacher.name}
                                </div>
                              </div>
                            ) : (
                              <div className="text-center text-muted" style={{ fontSize: "0.7rem", padding: "0.5rem 0" }}>
                                {tt.empty}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card.Body>
        </Card>
      )}
    </React.Fragment>
  );
}
